import {
  FOGBUGZ_FORMULATOR_IMAGE_MIME_TYPES,
  FOGBUGZ_TO_ADO_IMAGE_MAX_BYTES,
  fogbugzBugBarResultSchema,
  fogbugzToAdoClassificationSchema,
  fogbugzToAdoCreateResultSchema,
  fogbugzToAdoPrepareResponseSchema,
  type FogbugzBugBarResult,
  type FogbugzFormulatorImageMimeType,
  type FogbugzToAdoCaseBundle,
  type FogbugzToAdoClassification,
  type FogbugzToAdoCreateBody,
  type FogbugzToAdoCreateResult,
  type FogbugzToAdoImageInput,
  type FogbugzToAdoPmAddition,
  type FogbugzToAdoPrepareResponse,
  type FogbugzToAdoReporterQuestion,
  type FogbugzToAdoSlotVerdict,
} from "@ttc-spectree/toolbox-contract";
import { api } from "./client";

/**
 * Client for FogBugz to ADO (epic PI-SPEC-SW-E311, feature PI-SPEC-SW-1640,
 * ER-1666 Package E). Prefix `/api/v1/fogbugz-to-ado`:
 * - `POST /cases/:id/prepare`             read the case with the PM's FogBugz key: bundle or refusal
 * - `POST /cases/:id/bar-check`           the Bug bar on the confirmed slots and the PM's additions
 * - `POST /cases/:id/reporter-questions`  post questions for the reporter as one FogBugz note
 * - `GET  /projects/:project/classification`  area paths and iterations for the pickers
 * - `POST /cases/:id/create-bug`          create the reviewed Bug, copy the files, post the note
 *
 * Responses are parsed with the contract's lenient schemas. Case images for
 * Claude come from the existing authenticated FogBugz attachment proxy
 * (`GET /api/v1/fogbugz/attachment-proxy`, the website case page's download
 * route) at the `proxyPath` prepare gives each file, as base64, every image
 * on the case (no count cap); an image over `FOGBUGZ_TO_ADO_IMAGE_MAX_BYTES`
 * is skipped for Claude (never read past the cap); it is over the per-file
 * attachment cap too, so it stays on the FogBugz case.
 */

interface DataResponse<T> {
  data: T;
}

/** The api client's base is `/api/v1`. */
export const FOGBUGZ_TO_ADO_API_PATH = "/fogbugz-to-ado";

function casePath(caseId: number, action: string): string {
  return `${FOGBUGZ_TO_ADO_API_PATH}/cases/${String(caseId)}/${action}`;
}

/** The bar-check body: one verdict per bug slot and the PM's additions. */
export interface FogbugzToAdoBarCheckBody {
  slots: FogbugzToAdoSlotVerdict[];
  pmAdditions: FogbugzToAdoPmAddition[];
}

export const fogbugzToAdoApi = {
  /** Read case `caseId` as the PM: `{ status: "ready", bundle }` or `{ status: "refused", refusal }`. */
  prepare: async (caseId: number): Promise<FogbugzToAdoPrepareResponse> => {
    const response = await api.post<DataResponse<unknown>>(casePath(caseId, "prepare"), {});
    return fogbugzToAdoPrepareResponseSchema.parse(response.data);
  },

  /** The Bug bar on the slots the PM confirmed plus their additions. Nothing is read or written. */
  barCheck: async (caseId: number, body: FogbugzToAdoBarCheckBody): Promise<FogbugzBugBarResult> => {
    const response = await api.post<DataResponse<unknown>>(casePath(caseId, "bar-check"), body);
    return fogbugzBugBarResultSchema.parse(response.data);
  },

  /** Post the reviewed questions to the case as one FogBugz note. Never writes to ADO. */
  postReporterQuestions: async (caseId: number, questions: FogbugzToAdoReporterQuestion[]): Promise<void> => {
    await api.post<DataResponse<unknown>>(casePath(caseId, "reporter-questions"), { questions });
  },

  /** The ADO project's area paths and iterations (project root first), read with the PM's PAT. */
  classification: async (project: string): Promise<FogbugzToAdoClassification> => {
    const response = await api.get<DataResponse<unknown>>(
      `${FOGBUGZ_TO_ADO_API_PATH}/projects/${encodeURIComponent(project)}/classification`,
    );
    return fogbugzToAdoClassificationSchema.parse(response.data);
  },

  /** Create the reviewed Bug. The body is sent exactly as given. */
  createBug: async (caseId: number, body: FogbugzToAdoCreateBody): Promise<FogbugzToAdoCreateResult> => {
    const response = await api.post<DataResponse<unknown>>(casePath(caseId, "create-bug"), body);
    return fogbugzToAdoCreateResultSchema.parse(response.data);
  },
};

// ---------------------------------------------------------------------------
// Case images for Claude
// ---------------------------------------------------------------------------

const MIME_BY_EXTENSION: Readonly<Record<string, FogbugzFormulatorImageMimeType>> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
};

/** The image type Claude accepts for `fileName`, or null. Pure; exported for tests. */
export function imageMimeOf(fileName: string): FogbugzFormulatorImageMimeType | null {
  const lower = fileName.trim().toLowerCase();
  const dot = lower.lastIndexOf(".");
  if (dot < 0) return null;
  const mime = MIME_BY_EXTENSION[lower.slice(dot + 1)];
  return mime && (FOGBUGZ_FORMULATOR_IMAGE_MIME_TYPES as readonly string[]).includes(mime) ? mime : null;
}

/** `bytes` as base64, in chunks so a large image does not overflow the call stack. Pure; exported for tests. */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunk));
  }
  return btoa(binary);
}

/** A case image not sent to Claude, and why in plain words. */
export interface SkippedCaseImage {
  fileName: string;
  reason: string;
}

/** What {@link loadCaseImages} returns: the images for Claude and the ones it skipped. */
export interface CaseImages {
  images: FogbugzToAdoImageInput[];
  skipped: SkippedCaseImage[];
}

/**
 * The bytes at `path` (relative to `/api/v1`), or `"too-large"` once they pass
 * `maxBytes`: a `Content-Length` over the cap is refused before the body is
 * read, and a body without one is read in chunks and dropped (the request
 * aborted) as soon as it passes the cap, so an oversized file is never held
 * whole in memory.
 */
export async function readCapped(path: string, maxBytes: number): Promise<Uint8Array | "too-large"> {
  const controller = new AbortController();
  const response = await api.stream(path, { signal: controller.signal });
  const declared = Number(response.headers.get("Content-Length"));
  if (Number.isFinite(declared) && declared > maxBytes) {
    controller.abort();
    return "too-large";
  }
  const reader = response.body?.getReader();
  if (!reader) {
    const bytes = new Uint8Array(await response.arrayBuffer());
    return bytes.length > maxBytes ? "too-large" : bytes;
  }
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.length;
    if (total > maxBytes) {
      await reader.cancel().catch(() => undefined);
      controller.abort();
      return "too-large";
    }
    chunks.push(value);
  }
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.length;
  }
  return bytes;
}

/**
 * Every image on the case as Claude takes it (no count cap, owner decision
 * 2026-10-29): read through the authenticated FogBugz attachment proxy at the
 * file's `proxyPath` from prepare, with the PM's own key, as base64. An image
 * over `FOGBUGZ_TO_ADO_IMAGE_MAX_BYTES` (known from prepare or found while
 * reading, never read past the cap), of a type Claude cannot read, without a
 * download address or that could not be downloaded is skipped for Claude
 * (listed in `skipped`). One that is too large is over the per-file
 * attachment cap too, so it stays on the FogBugz case.
 */
export async function loadCaseImages(bundle: FogbugzToAdoCaseBundle): Promise<CaseImages> {
  const images: FogbugzToAdoImageInput[] = [];
  const skipped: SkippedCaseImage[] = [];
  for (const attachment of bundle.attachments.filter((candidate) => candidate.kind === "image")) {
    const mime = imageMimeOf(attachment.fileName);
    if (!mime || attachment.proxyPath === undefined) {
      skipped.push({ fileName: attachment.fileName, reason: "Claude cannot read this file" });
      continue;
    }
    if (attachment.sizeBytes > FOGBUGZ_TO_ADO_IMAGE_MAX_BYTES) {
      skipped.push({ fileName: attachment.fileName, reason: "too large for Claude to describe" });
      continue;
    }
    try {
      const bytes = await readCapped(attachment.proxyPath, FOGBUGZ_TO_ADO_IMAGE_MAX_BYTES);
      if (bytes === "too-large" || bytes.length === 0) {
        skipped.push({
          fileName: attachment.fileName,
          reason: bytes === "too-large" ? "too large for Claude to describe" : "the file is empty",
        });
        continue;
      }
      images.push({ fileName: attachment.fileName, mime, base64: bytesToBase64(bytes) });
    } catch {
      skipped.push({ fileName: attachment.fileName, reason: "it could not be downloaded from FogBugz" });
    }
  }
  return { images, skipped };
}
